<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  promptType: {
    type: String,
    default: 'text',
  },
  promptOptions: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();

const typeOptions = computed(() => [
  { value: 'text', label: t('FLOWS.PROMPT_TYPE_TEXT') },
  { value: 'single_select', label: t('FLOWS.PROMPT_TYPE_SINGLE') },
  { value: 'multi_select', label: t('FLOWS.PROMPT_TYPE_MULTI') },
]);

const optionRows = computed(() => {
  const o = props.promptOptions || [];
  const mapped = o.map(x => ({
    value: x.value ?? '',
    label: x.label ?? '',
  }));
  const pt = props.promptType || 'text';
  if (pt === 'text') return [];
  while (mapped.length < 2) {
    mapped.push({ value: '', label: '' });
  }
  return mapped;
});

const emitPayload = (type, rawOptions) => {
  if (type === 'text') {
    emit('update', { prompt_type: 'text', prompt_options: [] });
    return;
  }
  const cleaned = (rawOptions || []).map(o => ({
    value: String(o.value ?? '').trim(),
    label: String(o.label ?? '').trim(),
  }));
  emit('update', { prompt_type: type, prompt_options: cleaned });
};

const onTypeChange = e => {
  const type = e.target.value;
  if (type === 'text') {
    emitPayload('text', []);
    return;
  }
  const seed =
    (props.promptOptions || []).length >= 2
      ? props.promptOptions
      : [
          { value: '', label: '' },
          { value: '', label: '' },
        ];
  emitPayload(type, seed);
};

const syncRow = (index, field, value) => {
  const next = optionRows.value.map((r, i) =>
    i === index ? { ...r, [field]: value } : { ...r }
  );
  emitPayload(props.promptType || 'text', next);
};

const addOption = () => {
  emitPayload(props.promptType || 'single_select', [
    ...optionRows.value,
    { value: '', label: '' },
  ]);
};

const removeOption = index => {
  const next = optionRows.value.filter((_, i) => i !== index);
  emitPayload(props.promptType, next);
};
</script>

<template>
  <div
    class="rounded-xl border border-n-weak bg-n-alpha-1 p-4 dark:bg-n-alpha-2"
  >
    <p class="mb-3 text-heading-3 text-n-slate-12">
      {{ $t('FLOWS.PROMPT_SECTION_TITLE') }}
    </p>
    <div class="mb-4">
      <label
        class="mb-1 block text-label-small text-n-slate-11"
        for="flow-prompt-type"
      >
        {{ $t('FLOWS.PROMPT_TYPE_LABEL') }}
      </label>
      <select
        id="flow-prompt-type"
        class="block w-full max-w-md h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak focus:outline-n-brand"
        :value="promptType || 'text'"
        @change="onTypeChange"
      >
        <option v-for="opt in typeOptions" :key="opt.value" :value="opt.value">
          {{ opt.label }}
        </option>
      </select>
      <p class="mt-2 mb-0 text-xs text-n-slate-11">
        {{ $t('FLOWS.PROMPT_TYPE_HELP') }}
      </p>
    </div>

    <div v-if="promptType && promptType !== 'text'" class="space-y-3">
      <p class="text-label-small text-n-slate-11">
        {{ $t('FLOWS.PROMPT_OPTIONS_LABEL') }}
      </p>
      <div
        v-for="(row, index) in optionRows"
        :key="index"
        class="flex flex-wrap items-end gap-2 rounded-lg bg-n-background p-2 outline outline-1 outline-n-weak dark:bg-n-solid-1"
      >
        <div class="min-w-[6rem] flex-1">
          <Input
            :model-value="row.value"
            :label="$t('FLOWS.PROMPT_OPTION_VALUE')"
            size="sm"
            @update:model-value="syncRow(index, 'value', $event)"
          />
        </div>
        <div class="min-w-[6rem] flex-1">
          <Input
            :model-value="row.label"
            :label="$t('FLOWS.PROMPT_OPTION_LABEL')"
            size="sm"
            @update:model-value="syncRow(index, 'label', $event)"
          />
        </div>
        <Button
          v-if="optionRows.length > 2"
          faded
          ruby
          size="sm"
          icon="i-lucide-trash-2"
          :aria-label="$t('FLOWS.PROMPT_REMOVE_OPTION')"
          @click="removeOption(index)"
        />
      </div>
      <Button
        faded
        size="sm"
        icon="i-lucide-plus"
        :label="$t('FLOWS.PROMPT_ADD_OPTION')"
        @click="addOption"
      />
    </div>
  </div>
</template>
